import { ObjectType } from "common/constants/objectType";
import { camelCase, isNil } from "lodash";

/**
 * works like Object.assign except:
 * - returns a new object and does not mutate inputs
 * - values that are not undefined or null take precedence over undefined or
 * null values regardless of order
 */
type O = Record<string, unknown>;
function coalesceDefaults<T extends O, U extends O>(obj1: T, obj2: U): T & U;
function coalesceDefaults<T extends O, U extends O, V extends O>(
  obj1: T,
  obj2: U,
  obj3: V,
): T & U & V;
function coalesceDefaults<T extends O, U extends O, V extends O, W extends O>(
  obj1: T,
  obj2: U,
  obj3: V,
  obj4: W,
): T & U & V & W;
function coalesceDefaults<T extends O>(...objects: T[]): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const returnObj: Record<string, any> = {};
  objects.forEach((obj) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(obj).forEach(([key, val]: [string, any]) => {
      const destValue = returnObj[key];
      returnObj[key] = isNil(val) && !isNil(destValue) ? destValue : val;
    });
  });
  return returnObj as T;
}

export { coalesceDefaults };

export function DefaultDict<K extends string | symbol, V>(defaultFactory: () => V) {
  return new Proxy(Object.create(null) as Record<K, V>, {
    get(target, prop: K) {
      if (!(prop in target)) {
        // eslint-disable-next-line no-param-reassign
        target[prop] = defaultFactory();
      }
      return target[prop];
    },
  });
}

function isObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === "object" && !Array.isArray(obj);
}
/**
 * camelCase all keys for an object
 * @param input object to transform keys to camelCase
 * @returns transformed object with all top-level and deeply nested keys in camelCase
 */
export function camelCaseKeys<T extends Record<string, unknown>>(input: T): T {
  function recurse(input: unknown): unknown {
    if (Array.isArray(input)) {
      return input.map((el: unknown) => recurse(el));
    } else if (!input || !isObject(input)) {
      return input;
    }
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [camelCase(key), recurse(value)]),
    );
  }
  return recurse(input) as T;
}

/**
 * Removes the designated key from an object
 */
export function removeKey<T, K extends keyof T>(k: K, { [k]: _, ...rest }: T) {
  return rest;
}

/**
 *
 * @returns the display name of the object type with spaces between words
 */
export const getObjectTypeDisplayName = (objectType: ObjectType): string => {
  return ObjectType[objectType].replace(/([A-Z])/g, " $1").trim();
};

/**
 * Replaces narrow no-break spaces (`\u202F`) in the properties of an object with regular spaces.
 *
 * @param {unknown} obj - The object whose properties need space normalization.
 *                        It can be any value that is JSON serializable.
 *
 * @returns {unknown} A new object with all `\u202F` characters replaced by regular spaces.
 *                    The structure of the object is otherwise unchanged.
 *
 * @remarks
 * - This function is particularly useful when dealing with text generated by libraries or
 *   environments (e.g., ICU, Node.js date formatting) that use `\u202F` for spacing instead
 *   of a regular space (` `).
 * - It leverages JSON serialization (`JSON.stringify` and `JSON.parse`) to deeply traverse
 *   and modify all properties of the object.
 * - Non-JSON serializable inputs will result in an error; ensure the input is compatible with
 *   JSON.stringify.
 *
 * @note This function only normalizes spacing and does not modify any other part of the object's structure.
 */
export const normalizeSpaces = (obj: unknown): unknown => {
  return JSON.parse(
    JSON.stringify(obj).replace(/\u202F/g, " "), // Replace narrow no-break spaces with regular spaces
  );
};
